import { cn } from '@neo/lib/utils'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@neo/primitives/card'
import { ScrollAreaHorizontal } from '@neo/primitives/scroll-area'
import { ChatButtonTypes } from '@shared/types'
import { ExternalLink, Phone } from 'lucide-react'
import type { ChatMultiProduct } from '../types'
import { useSendMessage } from './use-send-message'
import { handleChatButtonClick, shouldDisableButton } from './utils/button-handlers'

interface ChatMultiProductCarouselProps {
  content: ChatMultiProduct
  isLastChunk: boolean
  /**
   * Fill the container instead of half of it. The default half-width suits the playground's
   * wider layout; the run panel is a narrow column where halving it cut the card off.
   */
  fullWidth?: boolean
}

type ProductItem = ChatMultiProduct['message'][0]['items'][0]

export const ChatMultiProductCarousel = ({
  content,
  isLastChunk,
  fullWidth = false,
}: ChatMultiProductCarouselProps) => {
  const { sendMessage, isSending } = useSendMessage()

  if (!content.message || content.message.length === 0) return null

  const { items } = content.message[0]
  if (!items || items.length === 0) return null

  const handleButtonClick = (
    buttonText: string,
    buttonType: string,
    buttonId: string,
    item: ProductItem,
  ) => {
    handleChatButtonClick(buttonText, buttonType, buttonId, item.buttons, sendMessage, isSending)
  }

  return (
    <div
      className={cn('min-w-0 max-w-full', fullWidth ? 'w-full' : 'w-1/2')}
      data-testid="container-chat-carousel"
    >
      <ScrollAreaHorizontal>
        <div className="flex gap-4 pb-2 items-start">
          {items.map((item) => (
            <Card
              key={item.id}
              className="w-[242px] flex-shrink-0 border border-border bg-card shadow-none overflow-hidden"
            >
              {item.header?.url && (
                <CardHeader className="p-0">
                  <img
                    src={item.header.url}
                    alt={item.header.name}
                    className="w-full h-40 object-cover rounded-t-lg"
                  />
                </CardHeader>
              )}
              <CardContent className="p-0 flex flex-col">
                <div className="flex flex-col gap-1 p-3">
                  {item.title && (
                    <CardTitle className="text-h4-500 text-foreground break-words">
                      {item.title}
                    </CardTitle>
                  )}
                  {item.description && (
                    <CardDescription className="font-inter text-s-400 text-secondary break-words">
                      {item.description}
                    </CardDescription>
                  )}
                </div>
                <div className="overflow-hidden border-t border-border rounded-b-lg">
                  {item.buttons.map((button, index) => {
                    return (
                      <button
                        key={button.id}
                        type="button"
                        disabled={shouldDisableButton(button.button_type, isLastChunk, isSending)}
                        data-testid={`chat-multi-product-button-${button.id}`}
                        onClick={() =>
                          handleButtonClick(button.text, button.button_type, button.id, item)
                        }
                        className={cn(
                          'w-full font-inter text-s-400 flex items-center justify-center p-2 text-foreground hover:bg-muted transition-colors break-words [overflow-wrap:anywhere] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                          index !== 0 && 'border-t',
                          shouldDisableButton(button.button_type, isLastChunk, isSending) &&
                            'opacity-50 cursor-not-allowed',
                        )}
                      >
                        {button.button_type === ChatButtonTypes.CALL && (
                          <Phone className="mr-1" size={12} />
                        )}
                        {button.text}
                        {button.button_type === ChatButtonTypes.WEBSITE && (
                          <ExternalLink className="ml-1" size={12} />
                        )}
                      </button>
                    )
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </ScrollAreaHorizontal>
    </div>
  )
}
